import React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Badge, DropdownItem, DropdownToggle, DropdownMenu } from "reactstrap";
import moment from "moment";
import "moment/locale/es";
import {
  getNotifications,
  readNotification,
  readAllNotifications,
} from "../../hooks/notifications";

/**
 * Notification bell in the header.
 *
 * Previously it badged the *total* number of notifications rather than the
 * unread ones, so the count never went down and there was no way to clear it —
 * clicking an item did nothing. It also keyed the list on `notification.
 * notificationId`, a field the API doesn't return, and never refetched, so new
 * notifications only appeared after a full page reload.
 */
const Notifications = () => {
  const userId = localStorage.getItem("userId");
  const queryClient = useQueryClient();

  const { data: notifications, isLoading } = useQuery(
    ["notifications"],
    () => getNotifications({ userId }),
    {
      // Poll so a notification sent while the page is open shows up without a
      // reload. 60s is frequent enough for this app and cheap (one small query).
      refetchInterval: 60000,
      refetchOnWindowFocus: true,
      enabled: Boolean(userId),
    }
  );

  const invalidate = () => {
    queryClient.invalidateQueries(["notifications"]);
    queryClient.invalidateQueries(["allNotifications"]);
  };

  const { mutate: markRead } = useMutation((variables) => readNotification({ variables }), {
    onSuccess: invalidate,
  });
  const { mutate: markAllRead } = useMutation(() => readAllNotifications(), {
    onSuccess: invalidate,
  });

  if (isLoading || !notifications) {
    return (
      <DropdownToggle nav>
        <i className="icon-bell"></i>
      </DropdownToggle>
    );
  }

  const unread = notifications.filter((n) => !n.read);

  return (
    <>
      <DropdownToggle nav>
        <i className="icon-bell"></i>
        {/* No badge at all when there's nothing unread — a grey "0" reads as a
            broken counter. */}
        {unread.length > 0 && (
          <Badge pill color="danger">
            {unread.length}
          </Badge>
        )}
      </DropdownToggle>
      <DropdownMenu right style={{ minWidth: "22rem", maxHeight: "26rem", overflowY: "auto" }}>
        <DropdownItem header tag="div" className="text-center">
          <strong>
            {unread.length > 0
              ? `Tienes ${unread.length} notificación(es) sin leer`
              : "No tienes notificaciones sin leer"}
          </strong>
        </DropdownItem>

        {notifications.length === 0 && (
          <DropdownItem disabled>
            <span className="text-muted">No hay notificaciones.</span>
          </DropdownItem>
        )}

        {notifications.slice(0, 15).map((notification) => (
          <DropdownItem
            key={notification.id}
            onClick={() => {
              if (!notification.read) markRead({ id: notification.id });
            }}
            style={{ whiteSpace: "normal" }}
          >
            <i
              className={`icon-user-follow ${notification.read ? "text-muted" : "text-success"}`}
            ></i>{" "}
            <span style={{ fontWeight: notification.read ? 400 : 600 }}>
              {notification.message}
            </span>
            <div className="small text-muted">
              {moment(notification.createdAt).locale("es").format("LLL")}
            </div>
          </DropdownItem>
        ))}

        {unread.length > 0 && (
          <>
            <DropdownItem divider />
            <DropdownItem
              className="text-center"
              onClick={(e) => {
                e.preventDefault();
                markAllRead();
              }}
            >
              Marcar todas como leídas
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </>
  );
};

export default Notifications;
